import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import PublicLayout from '../common/PublicLayout';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [token, setToken] = useState('');
  const [step, setStep] = useState('email');
  const { checkUserType, requestLoginToken, loginAdmin, verifyAdminMFA, verifyLoginToken } =
    useAuth();

  const isAdminAuth = step === 'password' || step === 'mfa';

  const handleEmailSubmit = async e => {
    e.preventDefault();
    try {
      const userType = await checkUserType(email);

      if (userType === 'admin') {
        setStep('password');
        navigate('/login', { state: { isAdmin: true }, replace: true });
      } else {
        await requestLoginToken(email);
        const demoToken = btoa(email);
        setToken(demoToken);
        setStep('member-message');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handlePasswordSubmit = async e => {
    e.preventDefault();
    try {
      await loginAdmin(email, password);
      setStep('mfa');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMFASubmit = async e => {
    e.preventDefault();
    try {
      await verifyAdminMFA(mfaCode);
      navigate('/admin');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleTokenSubmit = async e => {
    e.preventDefault();
    try {
      await verifyLoginToken(token);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (step === 'member-message') {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] max-w-md mx-auto text-center">
          <div className="bg-white/10 rounded-lg p-8 w-full">
            <h2 className="text-xl font-bold text-white mb-4">Check Your Email</h2>
            <p className="mb-4 text-white">
              We&apos;ve sent a login link to <strong>{email}</strong>
            </p>
            <p className="text-gray-300 mb-6">
              Click the link in the email to access the member portal. The link will expire in 15
              minutes.
            </p>
            <div className="mb-6 text-sm text-gray-400">
              <p>Demo Note: Token has been auto-populated for demonstration purposes.</p>
              <p>In production, this would be sent via email.</p>
            </div>
            <form className="space-y-4" onSubmit={handleTokenSubmit}>
              <div>
                <label htmlFor="demo-token" className="block mb-2 text-white">
                  Demo: Login Token
                </label>
                <input
                  id="demo-token"
                  type="text"
                  value={token}
                  onChange={e => setToken(e.target.value)}
                  className="w-full p-2 rounded bg-hdlcc-darkGray text-white border border-hdlcc-gray focus:border-hdlcc-lightBlue focus:outline-none"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-hdlcc-lightBlue text-white py-2 px-4 rounded hover:bg-hdlcc-blue"
              >
                Verify Token
              </button>
            </form>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const renderContent = () => {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white/10 rounded-lg p-8">
          <h2 className="text-xl font-bold text-white text-center mb-6">
            {isAdminAuth ? (step === 'password' ? 'Admin Login' : 'Enter MFA Code') : 'Sign In'}
          </h2>

          {step === 'email' && (
            <div className="mb-6 p-4 bg-white/5 rounded-lg">
              <h3 className="text-white font-semibold mb-2">Demo Login Options:</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <div>
                  <p className="font-medium text-hdlcc-lightBlue">Admin Access:</p>
                  <p>Email: admin@hdlcc.com</p>
                  <p>Password: password</p>
                  <p>MFA Code: 123456</p>
                </div>
                <div>
                  <p className="font-medium text-hdlcc-lightBlue">Member Access:</p>
                  <p>Email: member@hdlcc.com</p>
                  <p>(or use any other email)</p>
                </div>
              </div>
            </div>
          )}

          {step === 'email' && (
            <form className="space-y-4" onSubmit={handleEmailSubmit}>
              <div>
                <label htmlFor="email" className="block mb-2 text-white">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full p-2 rounded bg-hdlcc-darkGray text-white border border-hdlcc-gray focus:border-hdlcc-lightBlue focus:outline-none [&:-webkit-autofill]:!bg-hdlcc-darkGray [&:-webkit-autofill]:!text-white [&:-webkit-autofill]:![box-shadow:0_0_0_50px_rgb(26,26,26)_inset] [&:-webkit-autofill]:![transition-delay:0s] [&:-webkit-autofill]:![-webkit-text-fill-color:white]"
                  required
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-hdlcc-lightBlue text-white py-2 px-4 rounded hover:bg-hdlcc-blue transition-colors"
              >
                Continue
              </button>

              <p className="text-center text-sm text-gray-400">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="text-hdlcc-lightBlue hover:text-hdlcc-blue">
                  Sign up
                </Link>{' '}
                for a membership to get access.
              </p>
            </form>
          )}

          {step === 'password' && (
            <form className="space-y-4" onSubmit={handlePasswordSubmit}>
              <div>
                <label htmlFor="password" className="block mb-2 text-white">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full p-2 rounded bg-hdlcc-darkGray text-white border border-hdlcc-gray focus:border-hdlcc-lightBlue focus:outline-none [&:-webkit-autofill]:!bg-hdlcc-darkGray [&:-webkit-autofill]:!text-white [&:-webkit-autofill]:![box-shadow:0_0_0_50px_rgb(26,26,26)_inset] [&:-webkit-autofill]:![transition-delay:0s] [&:-webkit-autofill]:![-webkit-text-fill-color:white]"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-hdlcc-lightBlue text-white py-2 px-4 rounded hover:bg-hdlcc-blue transition-colors"
              >
                Sign In
              </button>
            </form>
          )}

          {step === 'mfa' && (
            <form className="space-y-4" onSubmit={handleMFASubmit}>
              <div>
                <label htmlFor="mfa-code" className="block mb-2 text-white">
                  MFA Code
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  className="w-full p-2 rounded bg-hdlcc-darkGray text-white border border-hdlcc-gray focus:border-hdlcc-lightBlue focus:outline-none [&:-webkit-autofill]:!bg-hdlcc-darkGray [&:-webkit-autofill]:!text-white [&:-webkit-autofill]:![box-shadow:0_0_0_50px_rgb(26,26,26)_inset] [&:-webkit-autofill]:![transition-delay:0s] [&:-webkit-autofill]:![-webkit-text-fill-color:white]"
                  required
                  placeholder="Enter the 6-digit code"
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-hdlcc-lightBlue text-white py-2 px-4 rounded hover:bg-hdlcc-blue transition-colors"
              >
                Verify
              </button>
            </form>
          )}
        </div>
      </div>
    );
  };

  return <PublicLayout>{renderContent()}</PublicLayout>;
}

export default Login;
